import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  faClock,
  faFire,
  faTrophy,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { API_ENDPOINTS } from "../../config/constants";
import { SectionCard } from "./index";
import { RecentPlateLink } from "./index";
import { SpotterRow } from "./index";
import VehicleLink from "../VehicleLink";

const HotPlates = ({ onSearch, autoHide = false }) => {
  const [data, setData] = useState({
    trending: [],
    topAllTime: [],
    recentlyAdded: [],
    leaderboard: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(autoHide);
  const { t } = useTranslation();

  useEffect(() => {
    setIsCollapsed(autoHide);
  }, [autoHide]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const controller = new AbortController();
      const signal = controller.signal;

      const fetchWithTimeout = async (endpoint) => {
        const response = await fetch(endpoint, {
          signal,
          credentials: "include",
        });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      };

      const [trending, topAllTime, recentlyAdded, leaderboard] =
        await Promise.all([
          fetchWithTimeout(API_ENDPOINTS.TRENDING_VEHICLES),
          fetchWithTimeout(API_ENDPOINTS.TOP_VEHICLES),
          fetchWithTimeout(API_ENDPOINTS.RECENT_VEHICLES),
          fetchWithTimeout(API_ENDPOINTS.LEADERBOARD),
        ]);

      setData({
        trending,
        topAllTime,
        recentlyAdded,
        leaderboard,
      });
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Fetch aborted");
        return;
      }
      console.error("Error fetching hot plates:", err);
      setError("Failed to load data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (error) {
    return (
      <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
    );
  }

  return (
    <div className="mt-4">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between bg-white dark:bg-dark-surface bg-gradient-to-b from-white to-gray-50 dark:from-dark-surface dark:to-primary-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-4 mb-2 border border-gray-100 dark:border-gray-700"
      >
        <h2 className="text-xl font-bold text-primary-700 dark:text-primary-100">
          {t("search.hot_plates_and_stats")}
        </h2>
        <span className="text-2xl text-primary-500 dark:text-primary-300">
          {isCollapsed ? "+" : "−"}
        </span>
      </button>

      {!isCollapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {isLoading ? (
            <div className="col-span-2 text-center py-8 text-gray-600 dark:text-gray-400">
              {t("common.loading")}
            </div>
          ) : (
            <>
              <SectionCard
                icon={faFire}
                iconColor="text-red-500"
                title={t("search.trending")}
                link="/explore?sort=trending"
                emptyMessage={t("search.no_trending")}
              >
                {data.trending.map((vehicle) => (
                  <div key={vehicle.plate}>
                    <VehicleLink vehicle={vehicle} />
                  </div>
                ))}
              </SectionCard>

              <SectionCard
                icon={faTrophy}
                iconColor="text-yellow-500"
                title={t("search.top_all_time")}
                link="/explore?sort=top"
                emptyMessage={t("search.no_top_plates")}
              >
                {data.topAllTime.map((vehicle) => (
                  <div key={vehicle.plate}>
                    <VehicleLink vehicle={vehicle} />
                  </div>
                ))}
              </SectionCard>

              <SectionCard
                icon={faClock}
                iconColor="text-blue-500"
                title={t("search.recently_added")}
                link="/explore?sort=recent"
                emptyMessage={t("search.no_recent_plates")}
              >
                <div className="grid grid-cols-3 gap-2">
                  {data.recentlyAdded.map((vehicle) => (
                    <RecentPlateLink
                      key={vehicle.plate}
                      plate={vehicle.plate}
                      createdAt={vehicle.createdAt}
                      plateStyle={vehicle.plateStyle || "malaysia-legacy"}
                    />
                  ))}
                </div>
              </SectionCard>

              <SectionCard
                icon={faUsers}
                iconColor="text-green-500"
                title={t("search.top_spotters")}
                link="/explore"
                emptyMessage={t("search.no_spotters")}
              >
                <div className="flex flex-col gap-4">
                  {/* Podium for top 3 */}
                  <div className="flex justify-center items-end mt-2 gap-2">
                    {/* Second Place */}
                    {data.leaderboard[1] && (
                      <div className="flex flex-col items-center w-28">
                        <div className="w-full h-20 flex items-end">
                          <SpotterRow
                            key={data.leaderboard[1]._id}
                            rank={2}
                            podium
                            username={data.leaderboard[1].username}
                            platesFound={data.leaderboard[1].platesFound}
                            profilePic={data.leaderboard[1].profilePic}
                            profilePicVariants={
                              data.leaderboard[1].profilePicVariants
                            }
                            spotterStats={data.leaderboard[1].spotterStats}
                          />
                        </div>
                        <div className="bg-gray-200 rounded-b-lg w-full h-6 flex items-center justify-center font-bold text-gray-700 text-xs truncate dark:text-gray-900">
                          {data.leaderboard[1].username}
                        </div>
                      </div>
                    )}
                    {/* First Place */}
                    {data.leaderboard[0] && (
                      <div className="flex flex-col items-center w-32">
                        <div className="w-full h-28 flex items-end">
                          <SpotterRow
                            key={data.leaderboard[0]._id}
                            rank={1}
                            podium
                            username={data.leaderboard[0].username}
                            platesFound={data.leaderboard[0].platesFound}
                            profilePic={data.leaderboard[0].profilePic}
                            profilePicVariants={
                              data.leaderboard[0].profilePicVariants
                            }
                            spotterStats={data.leaderboard[0].spotterStats}
                          />
                        </div>
                        <div className="bg-yellow-300 rounded-b-lg w-full h-8 flex items-center justify-center font-bold text-yellow-800 text-sm truncate shadow dark:text-yellow-900">
                          {data.leaderboard[0].username}
                        </div>
                      </div>
                    )}
                    {/* Third Place */}
                    {data.leaderboard[2] && (
                      <div className="flex flex-col items-center w-28">
                        <div className="w-full h-16 flex items-end">
                          <SpotterRow
                            key={data.leaderboard[2]._id}
                            rank={3}
                            podium
                            username={data.leaderboard[2].username}
                            platesFound={data.leaderboard[2].platesFound}
                            profilePic={data.leaderboard[2].profilePic}
                            profilePicVariants={
                              data.leaderboard[2].profilePicVariants
                            }
                            spotterStats={data.leaderboard[2].spotterStats}
                          />
                        </div>
                        <div className="bg-orange-300 rounded-b-lg w-full h-5 flex items-center justify-center font-bold text-orange-800 text-xs truncate dark:text-orange-900">
                          {data.leaderboard[2].username}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* 2 columns of 7 (ranks 4-17) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {data.leaderboard.slice(3, 17).map((user, index) => (
                      <SpotterRow
                        key={user._id}
                        rank={index + 4}
                        username={user.username}
                        platesFound={user.platesFound}
                        profilePic={user.profilePic}
                        profilePicVariants={user.profilePicVariants}
                        spotterStats={user.spotterStats}
                      />
                    ))}
                  </div>
                </div>
              </SectionCard>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(HotPlates);
